<div class="automation-rule-form lum-modal">
  <div class="lum-modal__header">
    <span class="lum-modal__header-title text-large">{{ mode }} Rule</span>
    <!-- Advanced mode toggle button -->
    <div class="lum-modal__header-extra">
      <button
        type="button"
        class="lum-button"
        [class.lum-button--primary]="isAdvancedMode"
        [class.lum-button--secondary]="!isAdvancedMode"
        (click)="toggleAdvancedMode()"
      >
        <icon
          [name]="isAdvancedMode ? 'settings' : 'gear-code-regular'"
          [size]="16"
        ></icon>
        {{ isAdvancedMode ? 'Standard View' : 'Advanced JSON View' }}
      </button>
    </div>
    <div class="lum-modal__header-close">
      <button mat-icon-button mat-dialog-close type="button" aria-label="Close">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <ng-template #errorSummary let-errors="errors">
    <ng-container *ngIf="errors?.length">
      <div class="alert alert-danger  my-4">
        <strong>Form Errors:</strong>
        <ul>
          <li *ngFor="let error of errors">{{ error }}</li>
        </ul>
      </div>
    </ng-container>
  </ng-template>

  <!-- Regular form view -->
  <form *ngIf="!isAdvancedMode && form" [formGroup]="form" (submit)="onSubmit()">
    <div class="lum-modal__body">
      <div class="form-group">
        <div class="lum-label">Name</div>
        <input
          type="text"
          class="lum-input"
          formControlName="name"
          required="true"
          [ngClass]="{
            error: form?.get('name')?.invalid && form?.get('name')?.touched,
          }"
        />
        <mat-error *ngIf="form?.get('name')?.invalid">
          <small>
            {{ form?.get('name') | validateField }}
          </small>
        </mat-error>
      </div>

      <div class="form-group">
        <div class="lum-label">Event</div>
        <mat-select
          class="lum-select"
          formControlName="event"
          [ngClass]="{
            error: form?.get('event')?.invalid && form?.get('event')?.touched,
          }"
        >
          <mat-option
            *ngFor="let event of automationRuleConfig.events; trackBy: trackById"
            [value]="event.id"
          >
            {{ event.name }}
          </mat-option>
        </mat-select>
        <mat-error *ngIf="form?.get('event')?.invalid">
          <small> {{ form?.get('event') | validateField }} </small>
        </mat-error>
      </div>

      <!-- CONDITIONS EXPANSION PANEL -->
      <div class="mb-4" *ngIf="hideConditionPanel">
        <mat-accordion>
          <mat-expansion-panel
            [expanded]="true"
            class="lum-mat-expansion-panel lum-mat-expansion-panel--border"
          >
            <mat-expansion-panel-header>
              <mat-panel-title> Conditions </mat-panel-title>
            </mat-expansion-panel-header>

            <lum-automation-rule-condition-form
              [form]="form"
              [customFields]="customFields"
              [ruleIds]="ruleIds"
            ></lum-automation-rule-condition-form>
          </mat-expansion-panel>
        </mat-accordion>
      </div>

      <!-- ACTIONS EXPANSION PANEL -->
      <div>
        <mat-accordion>
          <mat-expansion-panel
            [expanded]="true"
            class="lum-mat-expansion-panel lum-mat-expansion-panel--border"
          >
            <mat-expansion-panel-header>
              <mat-panel-title> Actions </mat-panel-title>
            </mat-expansion-panel-header>

            <lum-automation-rule-action-form
              [form]="form"
              [customFields]="customFields"
              [hasUserGroups]="userGroups?.length > 0"
            ></lum-automation-rule-action-form>
          </mat-expansion-panel>
        </mat-accordion>
      </div>

      <ng-container
        *ngTemplateOutlet="errorSummary; context: { errors: formErrors }"
      ></ng-container>
    </div>

    <mat-dialog-actions class="lum-modal__footer" align="end">
      <button
        type="button"
        class="lum-button lum-button--secondary"
        mat-button
        mat-dialog-close
        >Cancel</button
      >
      <lum-ui-button
        label="Save"
        [loading]="submitting"
        [disabled]="form?.invalid"
        (click)="onSubmit()"
      ></lum-ui-button>
    </mat-dialog-actions>
  </form>

  <!-- Advanced JSON view -->
  <form
    *ngIf="isAdvancedMode && jsonEditorForm"
    [formGroup]="jsonEditorForm"
    (submit)="onSubmit()"
  >
    <div class="lum-modal__body">
      <!-- Natural Language Input Section -->
      <div class="natural-language-section mb-4">
        <div class="natural-language-header mb-2">
          <h3>Create with Natural Language</h3>
          <p class="text-muted"
            >Describe your automation rule in plain English and we'll generate it
            for you.</p
          >
        </div>
        <div class="natural-language-input-container">
          <textarea
            class="lum-input natural-language-input"
            rows="4"
            placeholder="E.g., 'When an order is created with tag Custom, send an email notification to orders@example.com'"
            [(ngModel)]="naturalLanguageText"
            [ngModelOptions]="{ standalone: true }"
          ></textarea>
        </div>
        <div class="natural-language-actions mt-2 flex justify-end">
          <button
            type="button"
            class="lum-button lum-button--primary"
            [disabled]="!naturalLanguageText || naturalLanguageProcessing"
            (click)="generateRuleFromText()"
          >
            <lum-ui-icon
              *ngIf="!naturalLanguageProcessing"
              class="mr-2"
              icon="wand-2"
              width="16"
              height="16"
            ></lum-ui-icon>
            <span
              *ngIf="naturalLanguageProcessing"
              class="spinner-border spinner-border-sm mr-2"
              role="status"
              aria-hidden="true"
            ></span>
            {{ naturalLanguageProcessing ? 'Generating...' : 'Generate Rule' }}
          </button>
        </div>
        <div
          *ngIf="naturalLanguageError"
          class="natural-language-error mt-2 error-text"
        >
          {{ naturalLanguageError }}
        </div>
        <mat-divider class="my-4"></mat-divider>
      </div>

      <div class="form-group">
        <div class="lum-label">Raw JSON Rule</div>
        <div class="json-editor-help mb-2">
          <small>
            Advanced mode allows you to edit the raw JSON structure of automation
            rules. The JSON will be properly formatted with nested objects instead
            of escaped strings, making it easier to edit complex rules.
          </small>
        </div>
        <div class="json-editor-container">
          <textarea
            class="json-editor lum-input"
            formControlName="jsonRule"
            rows="20"
            [ngClass]="{
              error:
                jsonEditorForm?.get('jsonRule')?.invalid &&
                jsonEditorForm?.get('jsonRule')?.touched,
            }"
          ></textarea>
        </div>
        <mat-error
          *ngIf="jsonEditorForm?.get('jsonRule')?.invalid || jsonValidationError"
        >
          <small>{{ jsonValidationError || 'Invalid JSON format' }}</small>
        </mat-error>
        <div class="mt-2">
          <small class="text-muted">
            <strong>Note:</strong> When editing values, keep proper JSON structure
            with objects as needed. Remember that when you switch back to standard
            view, some complex changes might not be reflected properly.
          </small>
        </div>
      </div>
    </div>

    <ng-container
      *ngTemplateOutlet="errorSummary; context: { errors: jsonEditorFormErrors }"
    ></ng-container>

    <mat-dialog-actions class="lum-modal__footer" align="end">
      <button
        type="button"
        class="lum-button lum-button--secondary"
        mat-button
        mat-dialog-close
        >Cancel</button
      >
      <lum-ui-button
        label="Save"
        [loading]="submitting"
        [disabled]="jsonEditorForm?.invalid"
        (click)="onSubmit()"
      ></lum-ui-button>
    </mat-dialog-actions>
  </form>
</div>
